import type dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { BadgeProps } from '@mui/material';
import type { AccountList } from 'src/types/user';
import type { Theme, SxProps, CSSObject } from '@mui/material/styles';

import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import TableSortLabel from '@mui/material/TableSortLabel';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { Badge, styled, MenuItem, Autocomplete, InputAdornment } from '@mui/material';

import { useTranslate } from 'src/locales';
import { useGetLabels } from 'src/pages/contacts/hook/useGetLabels';
import { getContrastYIQ } from 'src/pages/chat/components/chat-label';

import { Iconify } from '../iconify';

const visuallyHidden: CSSObject = {
  border: 0,
  padding: 0,
  width: '1px',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
};

export type TableHeadCellProps = {
  id: string;
  label?: string;
  width?: CSSObject['width'];
  align?: 'left' | 'center' | 'right';
  sx?: SxProps<Theme>;
  filterable?: boolean;
};

export type TableHeadCustomProps = {
  orderBy?: string;
  rowCount?: number;
  sx?: SxProps<Theme>;
  numSelected?: number;
  order?: 'asc' | 'desc';
  headCells: TableHeadCellProps[];
  onSort?: (id: string) => void;
  onSelectAllRows?: (checked: boolean) => void;
  handleFilterChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    column: keyof AccountList
  ) => void;
  selectRowPremission?: boolean;
  sortFldList?: string[];
  isCampaignTamplate?: boolean;
  isEdit?: boolean;
  isDelete?: boolean;
  setDateTimeFilter?: any;
  isCampaining?: boolean;
  handleToggleSelectAll?: any;
  selectAllFilter?: any;
  count?: number;
  defaultBedgecondition?: boolean;
  selectedContact?: any;
  dateTimeFiltre: {
    id: string;
    fromDate: dayjs.Dayjs;
    toDate: dayjs.Dayjs;
  }[];
  setPage: any;
  moduleName?: string;
  resetFilterKey?: number;
};

export function TableHeadCustom(
  {
    sx,
    order,
    onSort,
    orderBy,
    headCells,
    rowCount = 0,
    numSelected = 0,
    onSelectAllRows,
    handleFilterChange,
    setDateTimeFilter,
    selectRowPremission = true,
    sortFldList,
    isCampaignTamplate = false,
    isEdit = false,
    isDelete = false,
    isCampaining = false,
    handleToggleSelectAll = () => {},
    selectAllFilter,
    count = 0,
    defaultBedgecondition,
    selectedContact,
    dateTimeFiltre = [],
    setPage,
    moduleName = '',
    resetFilterKey,
  }: TableHeadCustomProps,
  ref: React.Ref<{ handleRemoveAllFilter: () => void }>
) {
  const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    left: '-7px !important',
    '& .MuiBadge-badge': {
      top: 12,
      padding: '0 4px',
    },
  }));

  const {
    _recordList,
    isFetching,
    open: openList,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
    queryParamsHandler,
  } = useGetLabels();

  const { t } = useTranslate('translation');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedColumn, setSelectedColumn] = useState<TableHeadCellProps | null>(null);

  const [filterValue, setFilterValue] = useState<any>('');

  const [allFilterables, setAllFilterables] = useState<{ id: string; value: string }[]>([]);

  // ======================================
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    const filterableFields = headCells
      .filter((cell) => cell.filterable)
      .map((cell) => ({ id: cell.id, value: '' }));

    setAllFilterables(filterableFields);
  }, []);

  useEffect(() => {
    if (!selectedColumn) return;
    const selectedColumnValue =
      allFilterables.find((col) => col.id === selectedColumn.id)?.value ?? '';
    setFilterValue(selectedColumnValue);

    // set default value for dateTie field
    const selectedDTColumnValue = dateTimeFiltre.find((f) => f.id === selectedColumn.id) || {
      fromDate: null,
      toDate: null,
    };

    setFromDate(selectedDTColumnValue.fromDate);
    setToDate(selectedDTColumnValue.toDate);
  }, [anchorEl]);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>, column: TableHeadCellProps) => {
    if (selectedColumn?.id !== column.id) {
      setFilterValue('');
    }
    setSelectedColumn(column);
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleApplyFilter = () => {
    if (selectedColumn) {
      if (selectedColumn && dateFieldColumns.includes(selectedColumn.id)) {
        setPage(0);
        setDateTimeFilter((prev: any[]) => {
          const existingIndex = prev.findIndex((col) => col.id === selectedColumn.id);
          const updatedItem = { id: selectedColumn.id, fromDate, toDate };

          if (existingIndex !== -1) {
            // Update existing item
            return prev.map((col, index) => (index === existingIndex ? updatedItem : col));
          }

          // Add new item
          return [...prev, updatedItem];
        });

        handleClosePopover();
        return;
      }

      handleFilterChange(
        { target: { value: filterValue } } as React.ChangeEvent<HTMLInputElement>,
        selectedColumn?.id as keyof AccountList
      );

      setAllFilterables((prev) =>
        prev.map((item) =>
          item.id === selectedColumn?.id ? { ...item, value: filterValue } : item
        )
      );
      handleClosePopover();
    }
  };

  // ========================================================================
  const handleResetFilter = () => {
    if (selectedColumn) {
      if (selectedColumn && dateFieldColumns.includes(selectedColumn.id)) {
        setPage(0);

        setDateTimeFilter(
          (prev: any[]) => prev.filter((col) => col.id !== selectedColumn.id) // ✅ remove the filter item
        );

        handleClosePopover();
        return;
      }

      handleFilterChange(
        { target: { value: '' } } as React.ChangeEvent<HTMLInputElement>, // ✅ reset value to empty
        selectedColumn?.id as keyof AccountList
      );

      setAllFilterables((prev) =>
        prev.map(
          (item) => (item.id === selectedColumn?.id ? { ...item, value: '' } : item) // ✅ reset value
        )
      );

      handleClosePopover();
    }

    // ✅ Clear UI state
    setFilterValue('');
    setFromDate(null);
    setToDate(null);
    queryParamsHandler({ type: 'reset' });
  };

  // ========================================================================
  useEffect(() => {
    // whenever resetFilterKey changes, reset everything
    // ✅ Clear all filters here
    setAllFilterables((prev) => prev.map((f) => ({ ...f, value: '' })));
    setFilterValue('');
    setFromDate(null);
    setToDate(null);
    setAnchorEl(null);
    setSelectedColumn(null);
    setDateTimeFilter([]);
    queryParamsHandler({ type: 'reset' });
    headCells.forEach((cell) => {
      if (!['action', 'resendMail'].includes(cell.id)) {
        handleFilterChange(
          { target: { value: '' } } as React.ChangeEvent<HTMLInputElement>,
          cell.id as keyof AccountList
        );
      }
    });
    setPage(0);
  }, [resetFilterKey]);
  // ========================================================================

  const dateFieldColumns = [
    'callDate',
    'callDateTs',
    'updatedAt',
    'createdAt',
    'createdDate',
    'lastModified',
    'createdTime',
    'lastActivity',
    'date',
  ];
  const dropDownFields = ['status', 'priority', 'direction', 'channelType'];
  const customColumns = ['labels'];
  // const isDateFieldReady =
  //   (selectedColumn && dateFieldColumns.includes(selectedColumn.id)) || fromDate || toDate;
  const options =
    moduleName && moduleName in DROPDOWN_OPTIONS && selectedColumn?.id
      ? DROPDOWN_OPTIONS[moduleName as ModuleName][selectedColumn.id as FieldName<ModuleName>] || []
      : [];

  return (
    <>
      <TableHead sx={sx} className="myTableHead">
        <TableRow>
          {selectRowPremission && onSelectAllRows && (
            <TableCell padding="checkbox">
              {!isCampaining ? (
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={(event) => onSelectAllRows(event.target.checked)}
                  inputProps={{
                    id: 'all-row-checkbox',
                    'aria-label': 'Select all rows',
                  }}
                />
              ) : (
                !isCampaignTamplate && (
                  <StyledBadge
                    color="primary"
                    badgeContent={
                      selectAllFilter.active
                        ? selectAllFilter.filterType === 'not in'
                          ? count - selectAllFilter.ids.length
                          : defaultBedgecondition && selectedContact
                            ? selectedContact?.length
                            : count
                        : selectAllFilter.filterType === 'in'
                          ? selectAllFilter.ids.length
                          : null
                    }
                    invisible={!selectAllFilter.active && selectAllFilter.ids.length === 0}
                  >
                    <Checkbox
                      indeterminate={selectAllFilter.active && selectAllFilter.ids.length > 0}
                      checked={selectAllFilter.active && selectAllFilter.ids.length === 0}
                      onChange={handleToggleSelectAll}
                      inputProps={{
                        id: 'all-row-checkbox',
                        'aria-label': 'Select all rows',
                      }}
                    />
                  </StyledBadge>
                )
              )}
            </TableCell>
          )}
          {headCells.map((headCell) => {
            const isActionColumn = ['action', 'resendMail'].includes(headCell.id);
            const updateCondition =
              moduleName === 'TENANTS' ? isEdit && isDelete : isEdit || isDelete;
            if (isActionColumn && updateCondition) {
              return (
                <TableCell
                  key={headCell.id}
                  align={headCell.align || 'left'}
                  sx={{ width: headCell.width, ...headCell.sx }}
                >
                  {headCell.label}
                </TableCell>
              );
            }

            if (!isActionColumn) {
              return (
                <TableCell
                  key={headCell.id}
                  align={headCell.id === 'redirect' ? 'center' : headCell.align || 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                  sx={{
                    width: headCell.width,
                    textAlign: headCell.id === 'redirect' ? 'center' : undefined,
                    ...headCell.sx,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={headCell.id === 'redirect' ? 'center' : 'left'}
                  >
                    {(!sortFldList ||
                      sortFldList.length === 0 ||
                      sortFldList.includes(headCell.id)) &&
                    onSort ? (
                      <TableSortLabel
                        hideSortIcon
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => onSort(headCell.id)}
                      >
                        {headCell.label}
                        {orderBy === headCell.id && (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        )}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}

                    {headCell.filterable && (
                      <Badge
                        color="error"
                        variant="dot"
                        overlap="circular"
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        invisible={
                          !(
                            allFilterables.find((filterable) => headCell.id === filterable.id)
                              ?.value ||
                            (() => {
                              const dateFilter = dateTimeFiltre.find(
                                (filterable) => headCell.id === filterable.id
                              );
                              return dateFilter?.fromDate && dateFilter?.toDate;
                            })()
                          )
                        }
                      >
                        <IconButton
                          size="small"
                          sx={{ mx: 2 }}
                          onClick={(event) => handleOpenPopover(event, headCell)}
                        >
                          <Iconify icon="mdi:filter" />
                        </IconButton>
                      </Badge>
                    )}
                  </Box>
                </TableCell>
              );
            }

            return null;
          })}
        </TableRow>
      </TableHead>

      {/* Popover Filter Input */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{ padding: '15px 10px 5px 10px' }}
          display="flex"
          flexDirection="column"
          gap={1}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.stopPropagation();
              event.preventDefault();
              handleApplyFilter();
            }
          }}
        >
          {selectedColumn && dateFieldColumns.includes(selectedColumn.id) ? (
            <Box sx={{ display: 'flex' }}>
              <MobileDateTimePicker
                ampm
                format="DD/MM/YYYY hh:mm A"
                name="fromDate"
                label={t('FROM')}
                value={fromDate}
                onChange={(val) => {
                  if (!val) return setFromDate(null);
                  setFromDate(val);

                  // If toDate exists and is before the new fromDate, update toDate to match fromDate
                  if (toDate && toDate.isBefore(val)) {
                    setToDate(val);
                  }
                  return '';
                }}
                localeText={{
                  cancelButtonLabel: t('CANCEL'),
                  okButtonLabel: t('OK'),
                  toolbarTitle: t('SELECT_DATE_TIME'),
                }}
                slotProps={{
                  actionBar: {
                    actions: ['cancel', 'accept'],
                  },
                  textField: {
                    InputLabelProps: { shrink: true },
                    sx: { width: '100%', margin: '2px' },
                    InputProps: {
                      endAdornment: fromDate && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent the picker from reopening
                              setFromDate(null);

                              // Blur the input field manually
                              const input = e.currentTarget.closest('div')?.querySelector('input');
                              input?.blur();
                            }}
                            size="small"
                            sx={{ color: 'text.secondary' }}
                          >
                            <Iconify icon="solar:close-circle-bold" width={20} height={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  },
                }}
              />
              <MobileDateTimePicker
                ampm
                format="DD/MM/YYYY hh:mm A"
                name="toDate"
                label={t('TO')}
                value={toDate}
                onChange={(val) => {
                  if (!val || !fromDate) return setToDate(val);

                  const isSameDay = val.isSame(fromDate, 'day');

                  if (isSameDay && val.isBefore(fromDate)) {
                    // Set the time of toDate to match fromDate
                    const adjusted = val.hour(fromDate.hour()).minute(fromDate.minute());
                    setToDate(adjusted);
                  } else {
                    setToDate(val);
                  }
                  return '';
                }}
                minDateTime={fromDate ?? undefined}
                localeText={{
                  cancelButtonLabel: t('CANCEL'),
                  okButtonLabel: t('OK'),
                  toolbarTitle: t('SELECT_DATE_TIME'),
                }}
                slotProps={{
                  actionBar: {
                    actions: ['cancel', 'accept'],
                  },
                  textField: {
                    InputLabelProps: { shrink: true },
                    sx: { width: '100%', margin: '2px' },
                    InputProps: {
                      endAdornment: toDate && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setToDate(null);
                              const input = e.currentTarget.closest('div')?.querySelector('input');
                              input?.blur();
                            }}
                            size="small"
                            sx={{ color: 'text.secondary' }}
                          >
                            <Iconify icon="solar:close-circle-bold" width={20} height={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  },
                }}
              />
            </Box>
          ) : selectedColumn && dropDownFields.includes(selectedColumn.id) ? (
            <TextField
              select
              size="small"
              fullWidth
              label={selectedColumn.label}
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
            >
              {options.map((option: any) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </TextField>
          ) : selectedColumn && customColumns.includes(selectedColumn.id) ? (
            <Autocomplete
              disableClearable
              open={openList}
              onOpen={() => openListHandler()}
              onClose={() => closeListHandler()}
              options={_recordList}
              value={filterValue}
              slotProps={{
                listbox: {
                  onScroll: listScrollHandler,
                  sx: {
                    maxHeight: 200,
                    overflowY: 'auto',
                  },
                },
              }}
              onChange={(e, newValue) => {
                if (newValue) setFilterValue(newValue);
                // if (onChange) onChange(newValue);
              }}
              loading={isFetching}
              onInputChange={(e, val, reason) => {
                if (reason === 'input') searchValueHandler(val);
              }}
              getOptionLabel={(option: any) => option?.labelName || ''}
              isOptionEqualToValue={(option: any, val: any) => option?.id === val?.id}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={t('LABELS')}
                  placeholder={t('SEARCH')}
                  InputProps={{
                    ...params.InputProps,
                    // endAdornment: null,
                  }}
                />
              )}
              renderOption={(props, option) => (
                <MenuItem
                  {...props}
                  key={option?.id}
                  disabled={option?.id === 'no-label'}
                  style={{
                    backgroundColor: option?.labelColor,
                    color: getContrastYIQ(option?.labelColor),
                    padding: '8px',
                  }}
                >
                  {option?.labelName}
                </MenuItem>
              )}
              fullWidth
            />
          ) : (
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder={`${t('FILTER')} ${selectedColumn?.label}`}
              value={filterValue}
              onChange={(e) => {
                const newValue = e.target.value.trim();
                if (newValue.length === 0) {
                  return setFilterValue(''); // Exit early
                }
                setFilterValue(e.target.value);
                return '';
              }}
            />
          )}

          <Box display="flex" justifyContent="flex-end" gap={1}>
            {/* {selectedColumn &&
              [...dateFieldColumns, ...dropDownFields, ...customColumns].includes(
                selectedColumn.id
              ) && (
                <Button size="small" onClick={handleResetDateTime}>
                  {t('RESET')}
                </Button>
              )} */}

            <Button size="small" onClick={handleResetFilter}>
              {t('RESET')}
            </Button>
            <Button size="small" onClick={handleClosePopover}>
              {t('CANCEL')}
            </Button>
            <Button
              size="small"
              onClick={handleApplyFilter}
              disabled={
                selectedColumn && dateFieldColumns.includes(selectedColumn.id)
                  ? !!(fromDate && !toDate) || !!(!fromDate && toDate)
                  : false
              }
            >
              {t('APPLY')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

export const DROPDOWN_OPTIONS = {
  TICKET: {
    status: [
      { label: 'OPEN', value: 'open' },
      { label: 'IN_PROGRESS', value: 'in progress' },
      { label: 'ON_HOLD', value: 'on hold' },
      { label: 'COMPLETED', value: 'completed' },
    ],
    priority: [
      { label: 'LOW', value: 'low' },
      { label: 'NORMAL', value: 'normal' },
      { label: 'HIGH', value: 'high' },
    ],
  },
  CAMPAIGN: {
    status: [
      { label: 'CREATED', value: 'created' },
      { label: 'RUNNING', value: 'running' },
      { label: 'COMPLETED', value: 'completed' },
    ],
  },
  TENANTS: {
    status: [],
  },
  call: {
    status: [
      { label: 'FAILED  ', value: 'FAILED' }, //
      { label: 'ANSWERED ', value: 'ANSWERED' },
      { label: 'NO ANSWER', value: 'NO ANSWER' },
      { label: 'BUSY ', value: 'BUSY' },
      { label: 'CONGESTION ', value: 'CONGESTION' },
    ],
    direction: [
      { label: 'Internal', value: 'Internal' },
      { label: 'Incoming', value: 'Incoming' },
      { label: 'Outgoing', value: 'Outgoing' },
      { label: 'Transit', value: 'Transit' },
    ],
  },
  user: {
    status: [
      { label: 'ACTIVE', value: 'ACTIVE' },
      { label: 'INACTIVE', value: 'INACTIVE' },
    ],
  },
  team: {
    status: [
      { label: 'ACTIVE', value: 'ACTIVE' },
      { label: 'INACTIVE', value: 'INACTIVE' },
    ],
  },
  template: {
    status: [
      { label: 'APPROVED', value: 'APPROVED' },
      { label: 'REJECTED', value: 'REJECTED' },
    ],
  },
  conversation: {
    status: [
      { label: 'OPEN', value: 'OPEN' },
      { label: 'CLOSE', value: 'CLOSE' },
    ],
    channelType: [
      {
        label: 'WHATSAPP',
        value: 'WHATSAPP',
      },
      {
        label: 'EMAIL',
        value: 'EMAIL',
      },
      {
        label: 'LIVE_CHAT',
        value: 'LIVE_CHAT',
      },
      // {
      //   label: 'VOICE',
      //   value: 'VOICE',
      // },
    ],
  },
  inbox: {
    status: [
      { label: 'OPEN', value: 'OPEN' },
      { label: 'CLOSE', value: 'CLOSE' },
    ],
  },
} as const;

export type ModuleName = keyof typeof DROPDOWN_OPTIONS;
export type FieldName<M extends ModuleName> = keyof (typeof DROPDOWN_OPTIONS)[M];
